/**
 * Warehouse view of pricelists — READ-ONLY.
 * Lists every pricelist type and shows the current product prices per pricelist
 * so warehouse staff can consult them while preparing orders.
 *
 * Features:
 *  - Tabs per listino (Distributori, Ho.Re.Ca, ecc.)
 *  - Barra di ricerca grande su codice/descrizione
 *  - Filtro categoria multi-select (chips)
 *  - Click sulla miniatura foto → lightbox grande
 */
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { FileText, Search, ImageOff, X, Tag } from "lucide-react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { API, calculateBasePrice, calculateFinalPrice } from "../utils/format";
import { formatMoney } from "../utils/currency";
import TechSheetButton from "../components/TechSheetButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImage = (p) => {
  const path = p?.image_path || p?.image_url;
  if (!path) return null;
  return path.startsWith("http") ? path : `${BACKEND_URL}/api/files/${path}`;
};

// ============ LIGHTBOX ============
const PhotoLightbox = ({ src, alt, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pricelist-photo-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
        aria-label="Chiudi"
        data-testid="pricelist-photo-lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      {alt && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-2 rounded-full max-w-[80vw] truncate">
          {alt}
        </div>
      )}
    </div>,
    document.body
  );
};

const WarehousePricelistsView = () => {
  const [types, setTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { src, alt }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [t, p] = await Promise.all([
          axios.get(`${API}/pricelist-types`),
          axios.get(`${API}/products`, { params: { active_only: true, in_listino: "true" } }),
        ]);
        setTypes(t.data || []);
        setProducts(p.data || []);
        if ((t.data || []).length > 0) setActiveId(t.data[0].id);
      } catch (e) {
        // Errore di rete o sessione scaduta: tabella mostra "Caricamento..." → "Nessun prodotto"
        if (process.env.NODE_ENV !== "production") console.error("[WarehousePricelistsView] load failed:", e);
      }
      setLoading(false);
    })();
  }, []);

  // Categorie uniche presenti nei prodotti
  const allCategories = useMemo(() => {
    const set = new Set();
    products.forEach(p => { if (p.category_name) set.add(p.category_name); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "it"));
  }, [products]);

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedCategories([]);
  };

  const activeType = types.find(t => t.id === activeId);
  const filtered = products.filter(p => {
    // Filtro categoria multi-select
    if (selectedCategories.length > 0 && !selectedCategories.includes(p.category_name)) {
      return false;
    }
    // Filtro testo (codice + descrizione + categoria)
    if (search) {
      const s = search.toLowerCase();
      const haystack = [p.code, p.description, p.category_name].filter(Boolean).join(" ").toLowerCase();
      if (!haystack.includes(s)) return false;
    }
    return true;
  });

  const hasActiveFilters = search.length > 0 || selectedCategories.length > 0;

  return (
    <div className="space-y-4" data-testid="warehouse-pricelists-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#8A1C2A]" /> Listini Prezzi
          </h1>
          <p className="text-sm text-stone-600 mt-1">Consultazione in sola lettura — gestiti dall&apos;admin.</p>
        </div>
      </div>

      {/* Tabs by pricelist type */}
      <Card className="p-2 bg-white border-stone-200 flex flex-wrap gap-2">
        {types.length === 0 && <p className="p-3 text-stone-500 text-sm italic">Nessun listino configurato.</p>}
        {types.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveId(t.id)}
            data-testid={`pricelist-tab-${t.name}`}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border ${activeId === t.id ? "bg-[#8A1C2A] text-white border-[#8A1C2A]" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}
          >
            {t.name}
            {t.discount > 0 && <Badge className="ml-2 bg-orange-100 text-orange-800 border-orange-200">-{(t.discount * 100).toFixed(0)}%</Badge>}
          </button>
        ))}
      </Card>

      {activeType && (
        <Card className="p-3 bg-stone-50 border-stone-200 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <div><strong>Provv. default:</strong> {(activeType.commission * 100).toFixed(1)}%</div>
            <div><strong>Ricarico:</strong> {(activeType.markup * 100).toFixed(1)}%</div>
            <div><strong>Sconto listino:</strong> {(activeType.discount * 100).toFixed(1)}%</div>
          </div>
        </Card>
      )}

      {/* SEARCH BAR — grande, ben visibile */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca codice, descrizione o categoria..."
          className="h-12 pl-12 pr-12 text-base bg-white border-stone-300 focus-visible:ring-[#8A1C2A]/30 focus-visible:border-[#8A1C2A] shadow-sm"
          data-testid="pricelist-search"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700"
            aria-label="Cancella ricerca"
            data-testid="pricelist-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* FILTRO CATEGORIE — chips multi-select */}
      {allCategories.length > 0 && (
        <Card className="p-3 bg-white border-stone-200" data-testid="pricelist-category-filter">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-stone-700">
              <Tag className="w-4 h-4 text-[#8A1C2A]" />
              Filtra per categoria
              {selectedCategories.length > 0 && (
                <Badge className="bg-[#8A1C2A] text-white border-[#8A1C2A]">
                  {selectedCategories.length} selezionata{selectedCategories.length === 1 ? "" : "e"}
                </Badge>
              )}
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-xs font-medium text-[#8A1C2A] hover:underline"
                data-testid="pricelist-clear-filters"
              >
                Pulisci filtri
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {allCategories.map(cat => {
              const selected = selectedCategories.includes(cat);
              return (
                <button
                  key={cat}
                  onClick={() => toggleCategory(cat)}
                  data-testid={`pricelist-cat-chip-${cat}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                    selected
                      ? "bg-[#8A1C2A] text-white border-[#8A1C2A] shadow-sm"
                      : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50 hover:border-stone-400"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Result counter */}
      {!loading && (
        <div className="text-xs text-stone-500 px-1" data-testid="pricelist-result-count">
          {filtered.length} prodott{filtered.length === 1 ? "o" : "i"}
          {hasActiveFilters && <span className="text-stone-400"> · filtrato/i da {products.length}</span>}
        </div>
      )}

      {/* TABLE */}
      <Card className="p-0 overflow-hidden bg-white border-stone-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 text-left text-xs uppercase text-stone-600">
              <tr>
                <th className="p-2 w-16">Foto</th>
                <th className="p-2">Codice</th>
                <th className="p-2">Descrizione</th>
                <th className="p-2">Categoria</th>
                <th className="p-2">U.M.</th>
                <th className="p-2 text-right">Prezzo {activeType?.name || "—"}</th>
                <th className="p-2 w-24 text-center">Scheda</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-6 text-center text-stone-500">Caricamento...</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-stone-500 italic">Nessun prodotto</td></tr>}
              {!loading && filtered.map(p => {
                let price = 0;
                if (activeType) {
                  // Step 1: check fixed price override per (product, pricelist)
                  const priceOverride = (p.price_overrides || {})[activeType.id];
                  if (priceOverride != null && priceOverride > 0) {
                    price = priceOverride;
                  } else {
                    // Step 2: compute via formula (mirror of admin logic)
                    const commOverride = (p.commission_overrides || {})[activeType.id];
                    const discOverride = (p.discount_overrides || {})[activeType.id];
                    const effComm = commOverride != null ? commOverride : (activeType.commission || 0);
                    const effDisc = discOverride != null ? discOverride : (activeType.discount || 0);
                    const base = calculateBasePrice(p.acq || 0, p.bank || 0, p.marg || 0, p.extra || 0);
                    price = calculateFinalPrice(base, effComm, activeType.markup || 0, effDisc);
                  }
                }
                const imgUrl = resolveImage(p);
                return (
                  <tr key={p.id} className="border-t border-stone-100 hover:bg-stone-50">
                    <td className="p-2">
                      {imgUrl ? (
                        <button
                          type="button"
                          onClick={() => setLightbox({ src: imgUrl, alt: p.description })}
                          className="block w-10 h-10 rounded border border-stone-200 overflow-hidden hover:ring-2 hover:ring-[#8A1C2A]/40 transition-all cursor-zoom-in"
                          aria-label={`Ingrandisci foto ${p.description}`}
                          data-testid={`pricelist-img-${p.code}`}
                        >
                          <img src={imgUrl} alt={p.description} className="w-full h-full object-cover" loading="lazy" />
                        </button>
                      ) : (
                        <div className="w-10 h-10 rounded border border-dashed border-stone-200 bg-stone-50 flex items-center justify-center text-stone-300">
                          <ImageOff className="w-4 h-4" />
                        </div>
                      )}
                    </td>
                    <td className="p-2 font-mono text-xs">{p.code}</td>
                    <td className="p-2">{p.description}</td>
                    <td className="p-2">
                      {p.category_name ? <Badge variant="outline" className="border-stone-300 text-stone-600 text-xs">{p.category_name}</Badge> : <span className="text-stone-300 text-xs">—</span>}
                    </td>
                    <td className="p-2 text-xs">{p.unit || "Kg"}</td>
                    <td className="p-2 text-right font-bold text-[#8A1C2A]">{formatMoney(price, null)}</td>
                    <td className="p-2 text-center">
                      <TechSheetButton productId={p.id} variant="icon" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {lightbox && (
        <PhotoLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
};

export default WarehousePricelistsView;
